"use client";

import { useRef, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@mdxui/primitives/lib/utils";
import { CodeBlock } from "../code-block/code-block";

interface MarkdownContentProps {
  children: ReactNode;
  className?: string;
}

/**
 * Detects language from className (e.g., "language-typescript")
 * Maps common aliases to valid Shiki language identifiers
 */
function getLanguageFromClassName(className?: string): string {
  if (!className) return "typescript";
  const match = className.match(/language-(\w+)/);
  if (!match) return "typescript";

  const lang = match[1].toLowerCase();

  // Map common aliases to valid Shiki languages
  const languageMap: Record<string, string> = {
    jsx: "javascript",
    js: "javascript",
    ts: "typescript",
    tsx: "typescript",
    py: "python",
    sh: "bash",
    yml: "yaml",
  };

  return languageMap[lang] || lang;
}

interface CodeBlockPortal {
  id: string;
  language: string;
  code: string;
  container: HTMLElement;
}

/**
 * MarkdownContent wrapper that automatically enhances code blocks with syntax highlighting.
 *
 * Converts `<pre><code>` elements into CodeBlock components with:
 * - Syntax highlighting via Shiki
 * - Light/dark mode support
 * - Copy button
 * - Language detection from className (e.g., `language-typescript`)
 *
 * @example
 * ```tsx
 * <MarkdownContent>
 *   <p>Some text</p>
 *   <pre><code className="language-typescript">const x = 1</code></pre>
 * </MarkdownContent>
 * ```
 */
export function MarkdownContent({ children, className }: MarkdownContentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [portals, setPortals] = useState<CodeBlockPortal[]>([]);
  const processedPreElements = useRef(new WeakSet<Element>());

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Clear previous portals and processed elements when children change
    processedPreElements.current = new WeakSet();
    setPortals([]);

    // Use requestAnimationFrame to ensure ReactMarkdown has rendered into the DOM
    const raf = requestAnimationFrame(() => {
      // Find all pre elements that haven't been processed
      const preElements = container.querySelectorAll("pre");
      const newPortals: CodeBlockPortal[] = [];

      preElements.forEach((pre) => {
        // Skip if already processed
        if (processedPreElements.current.has(pre)) return;

        // Skip if already has Shiki classes (means it's already syntax highlighted)
        if (pre.classList.contains("shiki")) return;

        // Skip if inside a portal container (prevents nested CodeBlocks)
        if (pre.closest("[data-code-portal]")) return;

        const code = pre.querySelector("code");
        if (!code) return;

        // Mark as processed
        processedPreElements.current.add(pre);

        const language = getLanguageFromClassName(code.className);
        const codeContent = code.textContent || "";
        const id = `md-code-${Math.random().toString(36).slice(2)}`;

        // Create portal container
        const portalContainer = document.createElement("div");
        portalContainer.dataset.codePortal = id;
        portalContainer.className = "not-prose my-4";

        // Replace pre with portal container
        pre.parentNode?.replaceChild(portalContainer, pre);

        newPortals.push({
          id,
          language,
          code: codeContent,
          container: portalContainer,
        });
      });

      setPortals(newPortals);
    });

    return () => cancelAnimationFrame(raf);
  }, [children]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "prose max-w-none text-muted-foreground",
        "prose-headings:text-foreground prose-headings:scroll-mt-20",
        "prose-p:text-muted-foreground",
        "prose-strong:text-foreground",
        // Inline code styling (not code blocks)
        "prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none",
        // Links
        "prose-a:text-foreground prose-a:underline prose-a:underline-offset-2 prose-a:hover:text-muted-foreground",
        // Lists
        "prose-ul:text-muted-foreground prose-li:text-muted-foreground",
        // Blockquotes - dark mode compatible styling
        // Note: quotation marks are removed via CSS in styles.css (targets p::before/after inside blockquote)
        "prose-blockquote:border-l-border prose-blockquote:border-l-3 prose-blockquote:pl-4 prose-blockquote:text-foreground/70 prose-blockquote:not-italic prose-blockquote:font-normal",
        // Tables - dark mode compatible, reduced top margin
        "prose-table:border-collapse prose-table:w-full prose-table:my-0",
        "prose-th:bg-muted prose-th:text-foreground prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:text-left prose-th:font-semibold",
        "prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2 prose-td:text-muted-foreground",
        "even:prose-tr:bg-muted/50",
        className,
      )}
    >
      {children}
      {portals.map((portal) =>
        createPortal(
          <CodeBlock key={portal.id} language={portal.language}>
            {portal.code}
          </CodeBlock>,
          portal.container,
        ),
      )}
    </div>
  );
}
